import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Package,
  Users,
  Calendar,
} from "lucide-react";
import { InventorySummary, Transaction } from "@/types";
import { formatCurrency } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type Props = {
  alerts?: any[];
  transactions?: Transaction[];
  stockItems?: any[];
  setActiveTab: (v: string) => void;
  setShowTransactionForm: (v: boolean) => void;
  stockStats?: InventorySummary;
};

export default function StockDashboard({
  alerts = [],
  transactions = [],
  stockItems = [],
  setActiveTab,
  stockStats,
}: Props) {
  const { t } = useTranslation();

  // Top suppliers by value
  const supplierStats = stockItems.reduce(
    (acc, item) => {
      const supplier = item.supplier || "Unknown";
      if (!acc[supplier]) {
        acc[supplier] = { count: 0, value: 0 };
      }
      acc[supplier].count += 1;
      acc[supplier].value += item.quantity * item.cost_per_unit;
      return acc;
    },
    {} as Record<string, { count: number; value: number }>
  );

  const topSuppliers = (
    Object.entries(supplierStats) as [
      string,
      { count: number; value: number },
    ][]
  )
    .sort(
      (
        [, a]: [string, { count: number; value: number }],
        [, b]: [string, { count: number; value: number }]
      ) => b.value - a.value
    )
    .slice(0, 3);

  // Helper to safely format percentages (avoid NaN when denominator is zero)
  const formatPercent = (numerator: number, denominator: number) => {
    if (!denominator || denominator === 0) return "0.0%";
    return `${((numerator / denominator) * 100).toFixed(1)}%`;
  };

  return (
    <div className="space-y-6 mt-8">
      {/* Enhanced Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.Stats.TotalItems")}
                </p>
                <p className="text-2xl font-bold">{stockStats?.total}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("Stock Dashboard.Stats.InStock", {
                    count: stockStats?.inStock ?? 0,
                  })}
                </p>
              </div>
              <Package className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.Stats.TotalValue")}
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(stockStats?.totalValue ?? 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  <TrendingUp className="w-3 h-3 inline mr-1" />
                  {t("Stock Dashboard.Stats.CurrentInventory")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.Stats.LowStockItems")}
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stockStats?.lowStock}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("Stock Dashboard.Stats.NeedAttention")}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.Stats.OutOfStock")}
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {stockStats?.outOfStock}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("Stock Dashboard.Stats.UrgentRestock")}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stock Distribution & Top Suppliers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="h-full">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t("Stock Dashboard.Suppliers.TopSuppliers")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex flex-col justify-between h-full">
            <div className="space-y-3">
              {topSuppliers.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Users className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-3 text-sm font-medium text-foreground">
                    {t("Stock Dashboard.Suppliers.NoSuppliers")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("Stock Dashboard.Suppliers.NoSuppliersDesc")}
                  </p>
                </div>
              ) : (
                topSuppliers.map(([supplier, data]) => (
                  <div
                    key={supplier}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {supplier.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{supplier}</p>
                        <p className="text-xs text-muted-foreground">
                          {data.count} {t("Stock Dashboard.Suppliers.Items")}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {formatCurrency(data.value)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatPercent(data.value, stockStats?.totalValue ?? 0)}{" "}
                        {t("Stock Dashboard.Suppliers.ofTotal")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="h-full">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4" />
              {t("Stock Dashboard.RecentAlerts")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 flex flex-col justify-between h-full">
            <div className="space-y-2">
              {alerts.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <AlertCircle className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm font-medium text-foreground">
                    {t("Stock Dashboard.NoActiveAlerts")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("Stock Dashboard.NoActiveAlertsDesc")}
                  </p>
                </div>
              ) : (
                alerts.slice(0, 3).map((alert) => (
                  <div
                    key={alert.id}
                    className="flex items-center justify-between p-2 border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setActiveTab("alerts")}
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      <div>
                        <p className="text-sm font-medium flex items-center gap-2">
                          {alert.Stock.name}
                          {alert.severity === "high" && (
                            <Badge
                              variant="destructive"
                              className="text-xs px-1 py-0"
                            >
                              {t("Stock Dashboard.Alerts.High")}
                            </Badge>
                          )}
                          {alert.severity === "medium" && (
                            <Badge
                              variant="outline"
                              className="text-xs px-1 py-0"
                            >
                              {t("Stock Dashboard.Alerts.Medium")}
                            </Badge>
                          )}
                        </p>

                        <p className="text-xs text-muted-foreground">
                          {Number(
                            alert.current_quantity ?? alert.current_qty ?? 0
                          )}{" "}
                          {alert.unit ||
                            alert.item_unit ||
                            t("Stock Dashboard.Transactions.Units")}{" "}
                          {t("Stock Dashboard.Remaining")}
                          {alert.alert_type === "out_of_stock"
                            ? ` - ${t("Stock Dashboard.Stats.OutOfStock")}`
                            : ` - ${t("Stock Dashboard.RunningLow")}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">→</div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Button
              onClick={() => setActiveTab("inventory")}
              variant="outline"
              size="sm"
              className="h-16 flex-col"
            >
              <Eye className="w-4 h-4 mb-2" />
              View All Stock
            </Button>
            <Button
              onClick={() => setActiveTab("transactions")}
              variant="outline"
              size="sm"
              className="h-16 flex-col"
            >
              <TrendingUp className="w-4 h-4 mb-2" />
              Recent Transactions
            </Button>
            <Button
              onClick={() => setShowTransactionForm(true)}
              variant="outline"
              size="sm"
              className="h-16 flex-col"
            >
              <Upload className="w-4 h-4 mb-2" />
              Record Transaction
            </Button>
            <Button
              onClick={() => setActiveTab("alerts")}
              variant="outline"
              size="sm"
              className="h-16 flex-col"
            >
              <AlertCircle className="w-4 h-4 mb-2" />
              Alerts ({alerts.filter((a) => !a.resolved).length})
            </Button>
            <Button
              onClick={() => setActiveTab("purchase-orders")}
              variant="outline"
              size="sm"
              className="h-16 flex-col"
            >
              <ShoppingCart className="w-4 h-4 mb-2" />
              Purchase Orders
            </Button>
            <Button
              onClick={() => setActiveTab("reports")}
              variant="outline"
              size="sm"
              className="h-16 flex-col"
            >
              <BarChart3 className="w-4 h-4 mb-2" />
              Reports & Analytics
            </Button>
          </div>
        </CardContent>
      </Card> */}

      {/* Transaction Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.RecentTransactions")}
                </p>
                <p className="text-2xl font-bold">{transactions.length}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("Stock Dashboard.InLast7Days", {
                    count: transactions.filter(
                      (t) =>
                        new Date(t.transaction_date || t.created_at) >=
                        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                    ).length,
                  })}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.TransactionValue")}
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    transactions
                      .filter(
                        (t) =>
                          (t.transaction_type === "stock_out" ||
                            t.transaction_type === "usage") &&
                          Number(t.total_cost || 0)
                      )
                      .reduce((sum, t) => sum + Number(t.total_cost || 0), 0)
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("Stock Dashboard.Transactions.StockOutCosts")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.TodaysActivity")}
                </p>
                <p className="text-2xl font-bold text-indigo-600">
                  {
                    transactions.filter(
                      (t) =>
                        new Date(
                          t.transaction_date || t.created_at
                        ).toDateString() === new Date().toDateString()
                    ).length
                  }
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("Stock Dashboard.TransactionsToday")}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Stock Dashboard.StockMovements")}
                </p>
                <p className="text-lg font-bold text-green-600">
                  +
                  {
                    transactions.filter(
                      (t) =>
                        t.transaction_type === "stock_in" ||
                        t.transaction_type === "purchase"
                    ).length
                  }
                </p>
                <p className="text-lg font-bold text-red-600">
                  -
                  {
                    transactions.filter(
                      (t) =>
                        t.transaction_type === "stock_out" ||
                        t.transaction_type === "usage"
                    ).length
                  }
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("Stock Dashboard.Transactions.InOutMovements")}
                </p>
              </div>
              <Package className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
            {t("Stock Dashboard.RecentTransactions")}
          </CardTitle>
          <button
            onClick={() => setActiveTab("transactions")}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline"
          >
            {t("Stock Dashboard.ViewAll")}
          </button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {transactions.slice(0, 5).map((transaction) => {
              const item = stockItems.find(
                (s) => s.id === transaction.stock_id
              );

              // Determine transaction type info
              const getTransactionTypeInfo = () => {
                switch (transaction.transaction_type) {
                  case "stock_in":
                    return {
                      color: "bg-green-500",
                      icon: "📈",
                      label: t("Stock Dashboard.Transactions.StockInLabel"),
                      textColor: "text-green-600",
                    };
                  case "stock_out":
                    return {
                      color: "bg-red-500",
                      icon: "📉",
                      label: t("Stock Dashboard.Transactions.StockOutLabel"),
                      textColor: "text-red-600",
                    };
                  case "manual_adjustment":
                    return {
                      color: "bg-blue-500",
                      icon: "⚙️",
                      label: t("Stock Dashboard.Transactions.AdjustmentLabel"),
                      textColor: "text-blue-600",
                    };
                  case "purchase":
                    return {
                      color: "bg-green-500",
                      icon: "🛒",
                      label: t("Stock Dashboard.Transactions.PurchaseLabel"),
                      textColor: "text-green-600",
                    };
                  case "usage":
                    return {
                      color: "bg-orange-500",
                      icon: "🍽️",
                      label: t("Stock Dashboard.Transactions.UsageLabel"),
                      textColor: "text-orange-600",
                    };
                  default:
                    return {
                      color: "bg-gray-500",
                      icon: "📄",
                      label: t("Stock Dashboard.Transactions.TransactionLabel"),
                      textColor: "text-gray-600",
                    };
                }
              };

              const typeInfo = getTransactionTypeInfo();
              const quantityChange = transaction.quantity
                ? (Number(transaction.quantity) >= 0 ? "+" : "-") +
                  transaction.quantity
                : transaction.previous_quantity && transaction.new_quantity
                  ? (
                      Number(transaction.new_quantity) -
                      Number(transaction.previous_quantity)
                    ).toString()
                  : "N/A";

              return (
                <div
                  key={transaction.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors gap-3 sm:gap-4"
                >
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1">
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div
                        className={`w-4 h-4 rounded-full ${typeInfo.color} flex items-center justify-center`}
                      >
                        <span className="text-xs text-white font-bold">
                          {typeInfo.icon}
                        </span>
                      </div>
                      <div className="text-xs text-center">
                        <Badge
                          variant={
                            transaction.status === "completed"
                              ? "default"
                              : transaction.status === "pending"
                                ? "secondary"
                                : "outline"
                          }
                          className="text-[10px] sm:text-xs px-1 py-0"
                        >
                          {transaction.status?.toUpperCase() || "N/A"}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                        <p className="font-medium text-sm sm:text-base truncate">
                          {item?.name ||
                            transaction.Stock?.name ||
                            t("Stock Dashboard.Transactions.UnknownItem")}
                        </p>
                        <Badge
                          variant="outline"
                          className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0 flex-shrink-0"
                        >
                          {typeInfo.label}
                        </Badge>
                      </div>

                      <div className="space-y-1">
                        {/* Quantity Change */}
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs">
                          <span className="text-muted-foreground">
                            {t("Stock Dashboard.Transactions.Quantity")}:
                          </span>
                          {transaction.previous_quantity &&
                          transaction.new_quantity ? (
                            <span className="font-medium">
                              {transaction.previous_quantity} →{" "}
                              {transaction.new_quantity}{" "}
                              {item?.unit ||
                                transaction.Stock?.unit ||
                                t("Stock Dashboard.Transactions.Units")}
                            </span>
                          ) : (
                            <span
                              className={`font-medium ${typeInfo.textColor}`}
                            >
                              {quantityChange}{" "}
                              {item?.unit ||
                                transaction.Stock?.unit ||
                                t("Stock Dashboard.Transactions.Units")}
                            </span>
                          )}
                        </div>

                        {/* Cost Information */}
                        {transaction.total_cost && (
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs">
                            <span className="text-muted-foreground">
                              {t("Stock Dashboard.Transactions.TotalCost")}:
                            </span>
                            <span className="font-medium text-green-600">
                              {formatCurrency(transaction.total_cost)}
                            </span>
                          </div>
                        )}

                        {/* Reference Type */}
                        {transaction.reference_type && (
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs">
                            <span className="text-muted-foreground">
                              {t("Stock Dashboard.Transactions.Reference")}:
                            </span>
                            <span className="font-medium">
                              {transaction.reference_type}
                            </span>
                          </div>
                        )}

                        {/* Notes */}
                        {transaction.notes && (
                          <div className="flex items-start gap-1 sm:gap-2 text-xs">
                            <span className="text-muted-foreground flex-shrink-0">
                              {t("Stock Dashboard.Transactions.Notes")}:
                            </span>
                            <span className="text-gray-600 line-clamp-2 sm:truncate sm:max-w-xs">
                              {transaction.notes}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-1 sm:text-right sm:ml-4 pt-2 sm:pt-0 border-t sm:border-t-0 w-full sm:w-auto">
                    <div className="flex items-center gap-1 text-xs sm:text-sm font-medium">
                      <Calendar className="w-3 h-3" />
                      <span className="sm:hidden">
                        {new Date(
                          transaction.transaction_date || transaction.created_at
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <span className="hidden sm:inline">
                        {new Date(
                          transaction.transaction_date || transaction.created_at
                        ).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(
                        transaction.transaction_date || transaction.created_at
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      {t("Stock Dashboard.Transactions.By")}{" "}
                      {transaction.User?.name || "Unknown"}
                    </span>
                    {transaction.approved_by && (
                      <span className="text-xs text-green-600 mt-1">
                        {t("Stock Dashboard.Transactions.ApprovedBy", {
                          name: transaction.approved_by,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {transactions.length === 0 && (
              <div className="text-center py-6 sm:py-8 text-muted-foreground">
                <TrendingUp className="mx-auto h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                <p className="mt-2 sm:mt-3 text-sm font-medium text-foreground">
                  {t("Stock Dashboard.NoRecentTransactions")}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("Stock Dashboard.NoRecentTransactionsDesc")}
                </p>
              </div>
            )}
          </div>

          {/* Transaction Summary Footer */}
          {transactions.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center mb-4">
                <div>
                  <p className="text-sm font-medium text-green-600">
                    {
                      transactions.filter(
                        (t) =>
                          t.transaction_type === "stock_in" ||
                          t.transaction_type === "purchase"
                      ).length
                    }
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {t("Stock Dashboard.StockIn")}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-600">
                    {
                      transactions.filter(
                        (t) =>
                          t.transaction_type === "stock_out" ||
                          t.transaction_type === "usage"
                      ).length
                    }
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {t("Stock Dashboard.StockOut")}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-600">
                    {formatCurrency(
                      transactions
                        .filter(
                          (t) =>
                            (t.transaction_type === "stock_out" ||
                              t.transaction_type === "stock_in" ||
                              t.transaction_type === "adjustment" ||
                              t.transaction_type === "usage") &&
                            Number(t.total_cost || 0)
                        )
                        .reduce((sum, t) => sum + Number(t.total_cost || 0), 0)
                    )}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {t("Stock Dashboard.TotalValueShort")}
                  </p>
                </div>
              </div>

              {/* Activity Timeline */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  {t("Stock Dashboard.RecentActivity")}
                </p>
                <div className="flex items-center gap-1">
                  {[...Array(7)].map((_, i) => {
                    const date = new Date();
                    date.setDate(date.getDate() - (6 - i));
                    const dayTransactions = transactions.filter((t) => {
                      const tDate = new Date(
                        t.transaction_date || t.created_at
                      );
                      return tDate.toDateString() === date.toDateString();
                    });

                    return (
                      <div
                        key={i}
                        className="flex-1 relative group"
                        title={`${date.toLocaleDateString()}: ${dayTransactions.length} transactions`}
                      >
                        <div
                          className={`h-2 rounded-sm ${
                            dayTransactions.length > 0
                              ? dayTransactions.length > 3
                                ? "bg-green-500"
                                : dayTransactions.length > 1
                                  ? "bg-blue-500"
                                  : "bg-gray-400"
                              : "bg-gray-200"
                          }`}
                        />
                        <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                          {dayTransactions.length}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t("Stock Dashboard.Timeline.SevenDaysAgo")}</span>
                  <span>{t("Stock Dashboard.Timeline.Today")}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
